/**
 * PAY-4392: which celebration a notice plays.
 *
 * Twenty construction scenes (PAY-4631, PAY-4644), each paired with a
 * palette. The same style is not picked twice in a row (`avoid`). This module
 * only CHOOSES. The scene (`celebration-scene.ts`) turns that choice and a
 * seed into a scene that draws the props and moves the notice: a crane hoists
 * it in, a dump truck tips it out. Pure and deterministic for a given seed.
 *
 * Palette NAMES are shared with Pay Mobile. The style list on mobile is
 * still its original two until that app catches up; these are the web set
 * the browser galleries signed off.
 */

/** How long one style plays before the canvas removes itself. */
export const CELEBRATION_DURATION_S = 7.2;

/** The web styles. PAY-4644 retired the particle styles (confetti and the rest). */
export const CELEBRATION_STYLES = [
  'bricks',
  'crane',
  'dump-truck',
  'concrete-pour',
  'excavator',
  'wrecking-ball',
  'forklift',
  'blueprint',
  'welding',
  'ribbon-cutting',
  'scaffolding',
  'pile-driver',
  'road-roller',
  'stop-slow',
  'surveyor',
  'tunnel-borer',
  'demolition',
  'tape-measure',
  'hammer-nails',
  'topping-out',
] as const;

export type CelebrationStyle = (typeof CELEBRATION_STYLES)[number];

/**
 * Curated palettes. Every colour is a mid-tone (Tailwind 400-500), saturated
 * enough to read on a white card and light enough to read on a dark one. The
 * NAMES are shared with Pay Mobile; the hex values are each client's own.
 */
export const CELEBRATION_PALETTES = {
  citrus: ['#F59E0B', '#FBBF24', '#84CC16', '#F97316'],
  lagoon: ['#06B6D4', '#3B82F6', '#14B8A6', '#38BDF8'],
  berry: ['#EC4899', '#A855F7', '#F43F5E', '#8B5CF6'],
  meadow: ['#22C55E', '#10B981', '#84CC16', '#EAB308'],
  sunset: ['#F97316', '#F43F5E', '#EC4899', '#F59E0B'],
} as const satisfies Record<string, readonly string[]>;

export type CelebrationPaletteName = keyof typeof CELEBRATION_PALETTES;

export const CELEBRATION_PALETTE_NAMES = Object.keys(
  CELEBRATION_PALETTES,
) as CelebrationPaletteName[];

/**
 * The palette each style was approved with. The scenes draw in their own site
 * colours; the palette tints what they throw (paperwork, confetti).
 */
export const STYLE_PALETTE = {
  bricks: 'sunset',
  crane: 'citrus',
  'dump-truck': 'citrus',
  'concrete-pour': 'lagoon',
  excavator: 'meadow',
  'wrecking-ball': 'berry',
  forklift: 'citrus',
  blueprint: 'lagoon',
  welding: 'sunset',
  'ribbon-cutting': 'citrus',
  scaffolding: 'citrus',
  'pile-driver': 'sunset',
  'road-roller': 'meadow',
  'stop-slow': 'sunset',
  surveyor: 'lagoon',
  'tunnel-borer': 'citrus',
  demolition: 'berry',
  'tape-measure': 'citrus',
  'hammer-nails': 'meadow',
  'topping-out': 'lagoon',
} as const satisfies Record<CelebrationStyle, CelebrationPaletteName>;

const LAST_STYLE_KEY = 'pay.update-celebration.last-style';

export interface CelebrationPlan {
  style: CelebrationStyle;
  paletteName: CelebrationPaletteName;
  /** Seed for {@link buildCelebrationScene}. */
  seed: number;
}

export type Rng = () => number;

/** mulberry32: small, fast, good enough for laying bricks. */
export function createRng(seed: number): Rng {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A 32-bit seed from any string (FNV-1a). */
export function seedFromString(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** A fresh seed for production: different every notice. */
export function randomSeed(): number {
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    return crypto.getRandomValues(new Uint32Array(1))[0]!;
  }
  return Math.floor(Math.random() * 4294967296);
}

export function isCelebrationStyle(value: unknown): value is CelebrationStyle {
  return (
    typeof value === 'string' &&
    (CELEBRATION_STYLES as readonly string[]).includes(value)
  );
}

/**
 * The style this browser played last. A notice is once per release, usually
 * days later and in a new tab, so sessionStorage would be empty and the
 * "not twice in a row" rule would not hold. A stored style that has left the
 * rotation (`aurora`, or a particle style such as `confetti`) is ignored.
 */
export function readLastCelebrationStyle(): CelebrationStyle | undefined {
  if (typeof localStorage === 'undefined') return undefined;
  try {
    const value = localStorage.getItem(LAST_STYLE_KEY);
    return isCelebrationStyle(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

export function rememberCelebrationStyle(style: CelebrationStyle) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(LAST_STYLE_KEY, style);
  } catch {
    // Private mode: the next notice may repeat. Nothing else depends on it.
  }
}

function pick<T>(rng: Rng, items: readonly T[]): T {
  return items[Math.min(items.length - 1, Math.floor(rng() * items.length))]!;
}

function pickStyle(rng: Rng, avoid?: CelebrationStyle): CelebrationStyle {
  const pool = avoid
    ? CELEBRATION_STYLES.filter((style) => style !== avoid)
    : CELEBRATION_STYLES;
  return pick(rng, pool);
}

/**
 * Plans one celebration. `options.style` / `options.palette` pin a choice
 * (tests, the dev override). `options.avoid` is skipped unless a style is
 * pinned. The palette follows the style unless pinned.
 */
export function planCelebration(
  rng: Rng,
  options: {
    style?: CelebrationStyle;
    palette?: CelebrationPaletteName;
    avoid?: CelebrationStyle;
  } = {},
): CelebrationPlan {
  const style = options.style ?? pickStyle(rng, options.avoid);
  const paletteName = options.palette ?? STYLE_PALETTE[style];
  const seed = Math.floor(rng() * 4294967296);
  return { style, paletteName, seed };
}
